import { inject, Injectable } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';

import { BarChartDatePeriod } from '../models';

@Injectable({
  providedIn: 'root',
})
export class FormatService {
  private readonly datePipe = inject(DatePipe);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly decimalPipe = inject(DecimalPipe);

  date(date: Date): string {
    return date ? this.datePipe.transform(date, 'shortDate') : undefined;
  }

  dateTime(date: Date): string {
    return date
      ? `${this.datePipe.transform(date, 'shortDate')} ${this.datePipe.transform(date, 'shortTime')}`
      : undefined;
  }

  dayOfWeekShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'EEE') : undefined;
  }

  dayOfWeekLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'EEEE') : undefined;
  }

  dayOfMonthShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'd') : undefined;
  }

  dayOfMonthLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'dd') : undefined;
  }

  monthShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'LLL') : undefined;
  }

  monthLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'LLLL') : undefined;
  }

  yearShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'yy') : undefined;
  }

  yearLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'yyyy') : undefined;
  }

  dateForPeriods(date: Date, period: BarChartDatePeriod): string {
    switch (period) {
      case BarChartDatePeriod.Week:
        return this.date(date);
      case BarChartDatePeriod.Month:
        return this.monthLong(date) + ' ' + this.yearLong(date);
      case BarChartDatePeriod.Year:
        return this.yearLong(date);
      default:
        return undefined;
    }
  }

  dateItemsForPeriod(date: Date, period: BarChartDatePeriod): string {
    switch (period) {
      case BarChartDatePeriod.Week:
        return this.dayOfWeekShort(date);
      case BarChartDatePeriod.Month:
        return this.dayOfMonthShort(date);
      case BarChartDatePeriod.Year:
        return this.monthShort(date);
      default:
        return undefined;
    }
  }

  number(value: number | string, decimalPlaces = 2): string {
    const valueNumber = this.parseNumberString(value);
    const roundValue = this.roundNumber(valueNumber, decimalPlaces);
    const digitsInfo = this.digitsInfo(decimalPlaces);
    return this.decimalPipe.transform(roundValue, digitsInfo);
  }

  numberUOM(value: number | string, unitOfMeasure: string, decimalPlaces = 2): string {
    return `${this.number(value, decimalPlaces)} ${unitOfMeasure}`;
  }

  percent(value: number | string, decimalPlaces = 2): string {
    return `${this.number(value, decimalPlaces)}%`;
  }

  currency(value: number | string, currencySymbol?: string, decimalPlaces = 2): string {
    const numberValue = this.parseNumberString(value);
    const roundValue = this.roundNumber(numberValue, decimalPlaces);
    const digitsInfo = this.digitsInfo(decimalPlaces);
    return this.currencyPipe.transform(roundValue, undefined, `${currencySymbol ?? ''} `, digitsInfo);
  }

  currencyUOM(value: number | string, currencySymbol: string, unitOfMeasure: string, decimalPlaces = 2): string {
    return `${this.currency(value, currencySymbol, decimalPlaces)} ${unitOfMeasure}`;
  }

  numberShort(value: number): number | string {
    if (value >= 1_000_000) {
      return `${this.roundNumber(value / 1_000_000, 2)}M`;
    } else if (value >= 1_000) {
      return `${this.roundNumber(value / 1_000, 2)}K`;
    }
    return value;
  }

  currencyShort(value: number, currencySymbol: string): string {
    return `${currencySymbol}${this.numberShort(value)}`;
  }

  multiLineToHtml(multiLine: string): string {
    return multiLine ? this.valuesToHtml(multiLine?.replace(/\r/g, '').split('\n')) : '';
  }

  valuesToHtml(values: string[]): string {
    return values.join('<br />');
  }

  /**
   * Picks a size-reducing class so a long description still fits the two lines that
   * `.stock-label-description` clamps it to, instead of being cut off. Used from label
   * templates as `<!--descriptionSizeClass(productDescription)-->`.
   *
   * The thresholds are character counts that fit two lines at each size on the medium
   * labels; below the first threshold no class is added and the base size applies.
   */
  descriptionSizeClass(description: string): string {
    const length = description?.length ?? 0;
    if (length > 90) {
      return 'stock-label-description-x-small';
    }
    if (length > 60) {
      return 'stock-label-description-small';
    }
    return '';
  }

  roundNumber(value: number, decimalPlaces: number) {
    const roundNumber = Math.pow(10, decimalPlaces);
    return Math.round(value * roundNumber) / roundNumber;
  }

  parseNumberString(value: number | string): number {
    if (typeof value === 'string') {
      let digitSeparatorIndex = value.lastIndexOf('.');
      if (digitSeparatorIndex === -1) {
        digitSeparatorIndex = value.lastIndexOf(',');
      }
      if (digitSeparatorIndex !== -1) {
        const whole = value.slice(0, digitSeparatorIndex).replace(/[,.]/g, '');
        const decimal = value.slice(digitSeparatorIndex + 1);
        return +`${whole}.${decimal}`;
      } else {
        return +value;
      }
    }

    return isNaN(value) ? 0 : value;
  }

  formatStringArrayToString(stringArray: string[]): string {
    return stringArray.join('\n');
  }

  private digitsInfo(decimalPlaces: number) {
    return `1.${decimalPlaces}-${decimalPlaces}`;
  }
}
